/**
 * JournalActionModal — Extracted from ImmeublePortal.jsx (Batch #12 refactor).
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { Lock, Loader2, BookOpen, Check } from "lucide-react";
import { BaseActionModal, FieldLabel, API, authH } from "./shared";

// Constantes extraites de ImmeublePortal.jsx (Batch #14.1 — fix orphan refs après refactor #12)
const JOURNAL_PAYMENT_METHODS = [
  { value: "interac", label: "Interac e-Transfert" },
  { value: "cash", label: "Comptant" },
  { value: "cheque", label: "Chèque" },
  { value: "transfer", label: "Virement bancaire" },
  { value: "other", label: "Autre" },
];

const MONTHS_FR = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

export function JournalActionModal({ apartments, buildings, onClose, onReload }) {
  const [tab, setTab] = useState("conciliation");
  const currentYear = new Date().getFullYear();
  const [revenues, setRevenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const todayISO = new Date().toISOString().slice(0, 10);
  const currentMonth = new Date().getMonth() + 1;

  const [form, setForm] = useState({
    building_id: "",
    unit_id: "",
    tenant_name: "",
    payment_method: "interac",
    amount: "",
    rent_month: currentMonth, // 1-12
    rent_year: currentYear,
    paid_at: todayISO,
    notes: "",
  });

  // Batch #14.9 — Bug fix : les apartments utilisent property_id (pas building_id) côté mapDashboard.
  // Avant, le filter cherchait a.building_id → toujours [] → dropdown logement vide.
  const buildingApts = useMemo(
    () => apartments.filter((a) => !form.building_id || a.property_id === form.building_id || a.building_id === form.building_id),
    [apartments, form.building_id],
  );

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/immeuble/manual-revenues?year=${currentYear}`, { headers: authH() });
      setRevenues(r.data?.items || []);
    } catch (_e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.building_id) return toast.error("Sélectionnez un immeuble");
    if (!form.unit_id) return toast.error("Sélectionnez un logement");
    if (!form.tenant_name?.trim()) return toast.error("Nom du locataire requis");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Montant requis");
    if (!form.rent_month) return toast.error("Mois du loyer requis");
    setBusy(true);
    try {
      const unit = apartments.find((a) => a.id === form.unit_id);
      const monthLabel = `${MONTHS_FR[form.rent_month - 1]} ${form.rent_year}`;
      await axios.post(
        `${API}/portal/immeuble/manual-revenues`,
        {
          amount: Number(form.amount),
          payment_method: form.payment_method,
          tenant_name: form.tenant_name.trim(),
          unit_label: unit?.unit_label || "",
          unit_id: form.unit_id,
          building_id: form.building_id,
          paid_at: form.paid_at,
          rent_month_label: monthLabel,
          rent_month: form.rent_month,
          rent_year: form.rent_year,
          notes: form.notes?.trim() || null,
          source: "conciliation_manuelle",
        },
        { headers: authH() },
      );
      toast.success(`Conciliation enregistrée pour ${monthLabel}`);
      setForm((f) => ({
        ...f, amount: "", tenant_name: "", notes: "",
        paid_at: todayISO,
      }));
      fetchAll();
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <BaseActionModal
      title="Journal — Conciliation manuelle"
      subtitle={`Enregistrez les paiements reçus hors-Stripe (Interac, comptant, chèque). ${currentYear}`}
      tone="rose"
      icon={BookOpen}
      testId="immeuble-action-modal-journal"
      onClose={onClose}
      footer={null}
    >
      <div className="space-y-3">
        {/* Tabs épurés : Conciliation + Historique uniquement */}
        <div className="flex gap-1 border-b border-slate-200" data-testid="immeuble-journal-tabs">
          {[
            { id: "conciliation", label: "Conciliation manuelle" },
            { id: "history",      label: `Historique (${revenues.length})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition ${
                tab === t.id
                  ? "border-rose-500 text-rose-700"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`immeuble-journal-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "conciliation" && (
          <div className="space-y-2.5" data-testid="immeuble-journal-conciliation-form">
            <p className="text-[10px] text-slate-500 italic">
              <Lock className="w-3 h-3 inline mr-0.5" />
              Verrouillage à la soumission — seul l'admin pourra modifier après coup.
            </p>

            {/* Ligne 1 : Immeuble + Logement */}
            <div className="grid grid-cols-2 gap-2">
              <FieldLabel label="Immeuble" required>
                <select
                  value={form.building_id}
                  onChange={(e) => {
                    upd("building_id", e.target.value);
                    upd("unit_id", ""); // reset apt
                  }}
                  className="cc-input"
                  data-testid="immeuble-journal-building"
                >
                  <option value="">— Sélectionner —</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>{b.name || b.address_line}</option>
                  ))}
                </select>
              </FieldLabel>
              <FieldLabel label="Logement" required>
                <select
                  value={form.unit_id}
                  onChange={(e) => upd("unit_id", e.target.value)}
                  className="cc-input"
                  disabled={!form.building_id}
                  data-testid="immeuble-journal-unit"
                >
                  <option value="">
                    {!form.building_id
                      ? "Choisir un immeuble d'abord"
                      : buildingApts.length === 0
                      ? "Aucun logement créé"
                      : "— Sélectionner —"}
                  </option>
                  {buildingApts.map((a) => (
                    <option key={a.id} value={a.id}>Logement {a.unit_label || a.apt_number}</option>
                  ))}
                </select>
                {form.building_id && buildingApts.length === 0 && (
                  <p className="text-[10px] text-rose-600 mt-1" data-testid="immeuble-journal-unit-empty">
                    ⚠️ Aucun logement créé pour cet immeuble. Crée d'abord les unités via la tuile "Logements".
                  </p>
                )}
              </FieldLabel>
            </div>

            {/* Ligne 2 : Locataire + Méthode */}
            <div className="grid grid-cols-2 gap-2">
              <FieldLabel label="Locataire" required>
                <input
                  value={form.tenant_name}
                  onChange={(e) => upd("tenant_name", e.target.value)}
                  className="cc-input"
                  placeholder="Nom complet"
                  data-testid="immeuble-journal-tenant-name"
                />
              </FieldLabel>
              <FieldLabel label="Méthode de paiement" required>
                <select
                  value={form.payment_method}
                  onChange={(e) => upd("payment_method", e.target.value)}
                  className="cc-input"
                  data-testid="immeuble-journal-payment-method"
                >
                  {JOURNAL_PAYMENT_METHODS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </FieldLabel>
            </div>

            {/* Ligne 3 : Montant + Mois du loyer payé */}
            <div className="grid grid-cols-3 gap-2">
              <FieldLabel label="Montant ($)" required>
                <input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => upd("amount", e.target.value)}
                  className="cc-input font-mono"
                  data-testid="immeuble-journal-amount"
                />
              </FieldLabel>
              <FieldLabel label="Mois du loyer payé" required>
                <select
                  value={form.rent_month}
                  onChange={(e) => upd("rent_month", Number(e.target.value))}
                  className="cc-input"
                  data-testid="immeuble-journal-rent-month"
                >
                  {MONTHS_FR.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </FieldLabel>
              <FieldLabel label="Année">
                <select
                  value={form.rent_year}
                  onChange={(e) => upd("rent_year", Number(e.target.value))}
                  className="cc-input"
                  data-testid="immeuble-journal-rent-year"
                >
                  {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </FieldLabel>
            </div>

            {/* Ligne 4 : Date du paiement */}
            <FieldLabel label="Date du paiement reçu" required>
              <input
                type="date"
                value={form.paid_at}
                onChange={(e) => upd("paid_at", e.target.value)}
                className="cc-input"
                data-testid="immeuble-journal-paid-at"
              />
            </FieldLabel>

            {/* Ligne 5 : Note */}
            <FieldLabel label="Note (optionnel)">
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => upd("notes", e.target.value)}
                maxLength={300}
                className="cc-input"
                placeholder="Référence de transfert, conditions particulières, etc."
                data-testid="immeuble-journal-notes"
              />
            </FieldLabel>

            <button
              onClick={submit}
              disabled={busy}
              className="w-full py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
              data-testid="immeuble-journal-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Enregistrer la conciliation
            </button>
          </div>
        )}

        {tab === "history" && (
          <div data-testid="immeuble-journal-history">
            {loading ? (
              <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>
            ) : revenues.length === 0 ? (
              <p className="text-xs italic text-slate-400 py-4 text-center">Aucune conciliation enregistrée pour {currentYear}.</p>
            ) : (
              <ul className="space-y-1 max-h-[340px] overflow-y-auto" data-testid="immeuble-journal-history-list">
                {revenues.map((r) => (
                  <li key={r.id} className="flex items-center gap-2 text-[11px] bg-slate-50 border border-slate-200 px-2 py-1.5 rounded">
                    <Lock className="w-2.5 h-2.5 text-slate-400 shrink-0" />
                    <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">{(r.paid_at || "").slice(0,10)}</span>
                    <span className="flex-1 min-w-0 truncate">
                      <b>{r.tenant_name || "—"}</b>
                      {r.unit_label && <span className="text-slate-500"> · Apt {r.unit_label}</span>}
                      {r.rent_month_label && <span className="text-slate-500"> · {r.rent_month_label}</span>}
                    </span>
                    <span className="text-[10px] text-slate-500 px-1.5 bg-white border rounded">{r.payment_method}</span>
                    <span className="font-mono font-bold text-slate-800 whitespace-nowrap">{Number(r.amount).toFixed(2)} $</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </BaseActionModal>
  );
}
